import { useContext, useEffect, useRef, useState } from "react";
import PageHeading from "../../Components/PageHeading/PageHeading";
import { UserAuthContext } from "../../Contexts/UserAuthContext";
import moneyExchangeIcon from "../../Assets/Images/transaction-data.png";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@mui/material";
import { Spin } from "antd";
import axios from "axios";
import swal from "sweetalert";
import Swal from "sweetalert2";

export default function DeveloperAi() {
    const { AIuser, userRefetch, setUserRefetch } = useContext(UserAuthContext);
    const navigate = useNavigate();
    const location = useLocation();

    const CREDITS_PER_PROJECT = 834;

    // State management
    const [projectName, setProjectName] = useState("");
    const [projectDescription, setProjectDescription] = useState("");
    const [currentStep, setCurrentStep] = useState(1);
    const [projectId, setProjectId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [answers, setAnswers] = useState([]);
    const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [projectStatus, setProjectStatus] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [isContinuing, setIsContinuing] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const timerRef = useRef(null);
    const statusRef = useRef(null);


    const creditToLocalString = AIuser?.credits
        ? parseInt(Math.floor(AIuser?.credits)).toLocaleString()
        : "0";

    // Get auth token
    const getAuthToken = () => {
        return localStorage.getItem("aiUser");
    };

    const handleSubmitCredits = async (creditsToDeduct) => {
        const deductedCredits = AIuser.credits - creditsToDeduct;

        try {
            await axios.put(
                "https://backend.whalesai.com/api/dashboard/user_signup-signin/update-credits",
                { email: AIuser?.email, credits: deductedCredits },
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );
            setUserRefetch(!userRefetch);
        } catch (err) {
            console.log(err);
        }
    };

    // Handle back to step 1
    const handleBackToStep1 = () => {
        setCurrentStep(1);
        setProjectId(null);
        setQuestions([]);
    };

    // Handle project creation (Step 1)
    const handleCreateProject = async () => {
        if (!projectName.trim() || !projectDescription.trim()) {
            return;
        }

        setIsLoading(true);

        try {
            // Step 1: Create project
            const createResponse = await axios.post(
                "https://backend.whalesai.com/api/v1/ai-developer/projects",
                {
                    projectName: projectName,
                    description: projectDescription
                },
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );

            if (createResponse.data.success) {
                const newProjectId = createResponse.data.data._id;
                setProjectId(newProjectId);

                // Step 2: Fetch clarifying questions
                const questionsResponse = await axios.get(
                    `https://backend.whalesai.com/api/v1/ai-developer/projects/${newProjectId}/questions`,
                    {
                        headers: {
                            Authorization: `${getAuthToken()}`,
                        },
                    }
                );

                if (questionsResponse.data.success) {
                    setQuestions(questionsResponse.data.data);
                    setAnswers(new Array(questionsResponse.data.data.length).fill(""));
                    setCurrentStep(2);
                } else {
                    throw new Error("Failed to fetch questions");
                }
            } else {
                throw new Error("Failed to create project");
            }
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: error.response?.data?.message || "Something went wrong! Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleAnswerChange = (index, value) => {
        const newAnswers = [...answers];
        newAnswers[index] = value;
        setAnswers(newAnswers);
    };

    // Add function to submit answers:
    const handleSubmitAnswers = async () => {
        // Check if all answers are filled
        const allAnswersFilled = answers.every(answer => answer.trim() !== "");

        if (!allAnswersFilled) {
            swal({
                text: "Please answer all questions before proceeding.",
                icon: "warning",
                buttons: "Ok",
                className: "modal_class_success",
            });
            return;
        }

        setIsSubmittingAnswers(true);

        try {
            const response = await axios.post(
                `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/answers`,
                {
                    answers: answers
                },
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );

            if (response.data.success) {
                setCurrentStep(4); // Move to generation step
            } else {
                throw new Error("Failed to submit answers");
            }
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: error.response?.data?.message || "Failed to submit answers. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsSubmittingAnswers(false);
        }
    };

    const handleGenerateClick = () => {
        // Check if user has enough credits
        if (AIuser.credits < CREDITS_PER_PROJECT) {
            swal({
                text: `You need ${CREDITS_PER_PROJECT} credits but only have ${Math.floor(AIuser.credits)} credits. Please buy more credits to continue.`,
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            }).then((value) => {
                if (value) {
                    navigate("/purchase");
                }
            });
            return;
        }

        // Show confirmation popup
        Swal.fire({
            title: "Generate Project",
            html: `
            <div style="text-align: center; padding: 10px;">
                <p style="margin: 10px 0;"><strong>Project:</strong> ${projectName}</p>
                <p style="margin: 10px 0;"><strong>Credits required:</strong> ${CREDITS_PER_PROJECT}</p>
                <p style="margin: 10px 0;"><strong>Your current balance:</strong> ${Math.floor(AIuser.credits)}</p>
                <p style="margin: 10px 0;"><strong>Balance after generation:</strong> ${Math.floor(AIuser.credits - CREDITS_PER_PROJECT)}</p>
            </div>
        `,
            icon: "info",
            background: "#001f3f",
            color: "#ffffff",
            showCancelButton: true,
            confirmButtonText: "Generate",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
        }).then((result) => {
            if (result.isConfirmed) {
                handleGenerateProject();
            }
        });
    };

    const handleGenerateProject = async () => {
        setIsGenerating(true);
        setElapsedTime(0);

        try {
            const response = await axios.post(
                `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/generate`,
                {},
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );

            if (response.data.success) {
                // Deduct credits
                await handleSubmitCredits(CREDITS_PER_PROJECT);

                // Log transaction (optional - add if you have transaction logging)
                const randomChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz01234567891011121314156655";
                let transactionId = "";
                for (let i = 0; i < 8; i++) {
                    transactionId += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
                }

                await axios.post(
                    "https://backend.whalesai.com/api/v1/credits-transaction/create",
                    {
                        aiType: "Developer AI",
                        transactionId: transactionId,
                        requestQues: `Project: ${projectName} - ${projectDescription}`,
                        response: projectId,
                        credit: CREDITS_PER_PROJECT,
                        userId: AIuser?._id,
                    }
                );

                // Start polling status
                startStatusPolling();
                // Start timer
                startTimer();
                setCurrentStep(5); // Move to status display step
            } else {
                throw new Error("Failed to start generation");
            }
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: error.response?.data?.message || "Failed to start project generation. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
            setIsGenerating(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location]);

    useEffect(() => {
        return () => {
            stopTimer();
            stopStatusPolling();
        };
    }, []);

    // Function to start timer:
    const startTimer = () => {
        if (timerRef.current) return;

        timerRef.current = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
    };

    // Function to stop timer:
    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    // Function to format elapsed time:
    const formatElapsedTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Function to poll status:
    const startStatusPolling = () => {
        if (statusRef.current) return;

        statusRef.current = setInterval(fetchProjectStatus, 5000);
    };

    // Function to stop polling:
    const stopStatusPolling = () => {
        if (statusRef.current) {
            clearInterval(statusRef.current);
            statusRef.current = null;
        }
    };

    // Function to fetch project status:
    const fetchProjectStatus = async () => {
        try {
            const response = await axios.get(
                `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/status`,
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );

            if (response.data.success) {
                setProjectStatus(response.data.data);

                // Check if completed
                if (response.data.data.status === "completed" && response.data.data.progress === 100) {
                    stopStatusPolling();
                    stopTimer();
                    setIsGenerating(false);
                }
            }
        } catch (error) {
            console.error("Error fetching status:", error);
        }
    };

    // Function to continue generation:
    const handleContinueGeneration = async () => {
        setIsContinuing(true);

        try {
            const response = await axios.post(
                `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/continue`,
                {},
                {
                    headers: {
                        Authorization: `${getAuthToken()}`,
                    },
                }
            );

            if (response.data.success) {
                // Resume polling
                if (!statusRef.current) {
                    startStatusPolling();
                }
                swal({
                    text: "Project generation resumed successfully!",
                    icon: "success",
                    buttons: "Ok",
                    className: "modal_class_success",
                });
            } else {
                throw new Error("Failed to continue generation");
            }
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: error.response?.data?.message || "Failed to continue generation. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsContinuing(false);
        }
    };

    // Function to download project:
    const handleDownloadProject = () => {
        setIsDownloading(true);

        try {
            // Create download URL
            const downloadUrl = `https://backend.whalesai.com/api/v1/ai-developer/projects/${projectId}/download`;

            const link = document.createElement("a");
            link.href = downloadUrl;
            link.target = "_blank";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            swal({
                text: "Download started successfully!",
                icon: "success",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } catch (error) {
            console.error("Error:", error);
            swal({
                text: "Failed to download project. Please try again.",
                icon: "error",
                buttons: "Ok",
                className: "modal_class_success",
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div>
            <div className="h-100 companion-ai-container mb-4">
                <PageHeading
                    title={"DEVELOPER AI"}
                    description={
                        "The Developer AI is a tool designed to assist developers in creating and managing their applications."
                    }
                />

                <div className="container companion-ai-main">
                    <div
                        style={{
                            maxWidth: "872px",
                            paddingInline: "12px",
                            marginInline: "auto",
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-center centralize-credits-btn-sm mt-2">
                            <button className="credits-button">
                                CREDITS : {creditToLocalString}
                            </button>
                            <button
                                className="buy-credits-button"
                                onClick={() => navigate("/purchase")}
                            >
                                BUY CREDITS
                            </button>

                            <button
                                onClick={() => navigate("/usage")}
                                className="money-exchange-button"
                            >
                                <img src={moneyExchangeIcon} alt="moneyExchangeIcon" />
                            </button>
                        </div>

                        <p
                            className="text-white mb-0 mt-2 creditText text-center"
                            style={{ fontSize: "8px" }}
                        >
                            834 Credit per project
                        </p>

                        {/* Step 1: Project Name and Description */}
                        {currentStep === 1 && (
                            <div className="mt-4">
                                <div className="text-center mb-4">
                                    <h5 className="text-white">Create Your Project</h5>
                                    <p className="text-white" style={{ fontSize: "14px" }}>
                                        Enter your project details to get started
                                    </p>
                                </div>

                                <div className="mb-3">
                                    <label className="text-white mb-2" style={{ fontSize: "14px" }}>
                                        Project Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="p-2 px-3 w-100 text-white"
                                        style={{
                                            background: "#011C34",
                                            border: "2px solid #FFF",
                                            borderRadius: "12px",
                                            outline: "none",
                                        }}
                                        value={projectName}
                                        onChange={(e) => setProjectName(e.target.value)}
                                        placeholder="Enter project name..."
                                        disabled={isLoading}
                                    />
                                </div>

                                <div className="mb-4">
                                    <label className="text-white mb-2" style={{ fontSize: "14px" }}>
                                        Project Description  <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="p-2 px-3 w-100 text-white"
                                        style={{
                                            background: "#011C34",
                                            border: "2px solid #FFF",
                                            borderRadius: "12px",
                                            outline: "none",
                                            height: "120px",
                                            resize: "none",
                                        }}
                                        value={projectDescription}
                                        onChange={(e) => setProjectDescription(e.target.value)}
                                        placeholder="Describe your project..."
                                        rows="5"
                                        disabled={isLoading}
                                    />
                                </div>

                                {isLoading ? (
                                    <div className="text-center mb-5">
                                        <Spin size="large" className="pt-3 pb-2" />
                                        <p className="text-center pb-2 d-block text-white mb-0">
                                            Creating your project... Please wait.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center mb-5">
                                        <Button
                                            sx={{
                                                padding: "8px 30px",
                                                marginRight: "10px",
                                                color: "#000",
                                                background: "#fff",
                                                "&:hover": { background: "#fff" },
                                                "&:disabled": { background: "#fff" },
                                            }}
                                            onClick={() => {
                                                setProjectName("");
                                                setProjectDescription("");
                                            }}
                                            disabled={!projectName && !projectDescription}
                                            variant="contained"
                                        >
                                            Clear
                                        </Button>
                                        <Button
                                            sx={{
                                                padding: "8px 30px",
                                                "&:disabled": { background: "#000" },
                                            }}
                                            onClick={handleCreateProject}
                                            className="generate-button text-white"
                                            disabled={!projectName.trim() || !projectDescription.trim()}
                                            variant="contained"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                )}

                                <div className="animate text-md-center mt-4 text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
                                    Please do not navigate away from the browser.
                                    <br /> Doing so will stop the process.
                                </div>
                                <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
                                    While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
                                </div>
                            </div>
                        )}

                        {/* Step 2: Clarifying Questions */}
                        {currentStep === 2 && (
                            <div className="mt-4">
                                <div className="text-center mb-4">
                                    <h5 className="text-white">Project Details</h5>
                                    <p className="text-white" style={{ fontSize: "14px" }}>
                                        Please answer the following questions about your project
                                    </p>
                                    <p className="text-warning" style={{ fontSize: "12px" }}>
                                        Project: {projectName}
                                    </p>
                                </div>

                                <div className="mb-4">
                                    {questions.map((question, index) => (
                                        <div key={index} className="mb-4">
                                            <div className="mb-2 p-3" style={{
                                                background: "#011C34",
                                                border: "2px solid #FFF",
                                                borderRadius: "12px",
                                            }}>
                                                <p className="text-white mb-0" style={{ fontSize: "14px" }}>
                                                    <strong>Question {index + 1}:</strong> {question}
                                                </p>
                                            </div>
                                            <textarea
                                                className="p-2 px-3 w-100 text-white"
                                                style={{
                                                    background: "#011C34",
                                                    border: "2px solid #FFF",
                                                    borderRadius: "12px",
                                                    outline: "none",
                                                    height: "80px",
                                                    resize: "none",
                                                }}
                                                value={answers[index]}
                                                onChange={(e) => handleAnswerChange(index, e.target.value)}
                                                placeholder="Enter your answer..."
                                                rows="3"
                                                disabled={isSubmittingAnswers}
                                            />
                                        </div>
                                    ))}
                                </div>

                                {isSubmittingAnswers ? (
                                    <div className="text-center mb-5">
                                        <Spin size="large" className="pt-3 pb-2" />
                                        <p className="text-center pb-2 d-block text-white mb-0">
                                            Submitting your answers... Please wait.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="d-flex justify-content-center align-items-center mb-5">
                                        <Button
                                            sx={{
                                                padding: "8px 30px",
                                                marginRight: "10px",
                                                color: "#000",
                                                background: "#fff",
                                                "&:hover": { background: "#fff" },
                                            }}
                                            onClick={handleBackToStep1}
                                            variant="contained"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            sx={{
                                                padding: "8px 30px",
                                                "&:disabled": { background: "#000" },
                                            }}
                                            onClick={handleSubmitAnswers}
                                            className="generate-button text-white"
                                            variant="contained"
                                        >
                                            Submit Answers
                                        </Button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Step 4: Generate Project */}
                        {currentStep === 4 && (
                            <div className="mt-4">
                                <div className="text-center mb-4">
                                    <div style={{
                                        background: "#011C34",
                                        border: "2px solid #FFF",
                                        borderRadius: "16px",
                                        padding: "50px 30px",
                                    }}>
                                        <div style={{
                                            width: "60px",
                                            height: "60px",
                                            background: "linear-gradient(135deg, #00ff88, #00cc70)",
                                            borderRadius: "50%",
                                            margin: "0 auto 25px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: "30px",
                                        }}>
                                            ✓
                                        </div>

                                        <h4 className="text-white mb-2" style={{ fontWeight: "600" }}>
                                            Ready to Generate
                                        </h4>

                                        <p className="text-white mb-4" style={{
                                            fontSize: "14px",
                                            opacity: 0.8,
                                        }}>
                                            Your project configuration is complete
                                        </p>

                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <span className="text-white" style={{ fontSize: "14px", opacity: 0.7 }}>
                                                Project:
                                            </span>
                                            <span className="text-warning" style={{
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                            }}>
                                                {projectName}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Credit Information Box */}
                                <div className="mb-4">
                                    <div style={{
                                        background: "#011C34",
                                        border: "2px solid #FFF",
                                        borderRadius: "12px",
                                        padding: "15px",
                                    }}>
                                        <p className="text-white mb-2">
                                            <strong>Credits Required:</strong> {CREDITS_PER_PROJECT} credits
                                        </p>
                                        <p className="text-white mb-2">
                                            <strong>Your Current Balance:</strong> {Math.floor(AIuser?.credits || 0)} credits
                                        </p>
                                        <p className="text-white mb-0">
                                            <strong>Balance After Generation:</strong> {Math.floor((AIuser?.credits || 0) - CREDITS_PER_PROJECT)} credits
                                        </p>
                                    </div>
                                </div>

                                <div className="d-flex justify-content-center align-items-center mb-5">
                                    <Button
                                        sx={{
                                            padding: "8px 30px",
                                            "&:disabled": { background: "#000" },
                                        }}
                                        onClick={handleGenerateClick}
                                        className="generate-button text-white"
                                        variant="contained"
                                        disabled={isGenerating}
                                    >
                                        {isGenerating ? "Starting..." : "Generate Project"}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 5: Generation Status */}
                        {currentStep === 5 && (
                            <div className="mt-4">
                                <div className="text-center mb-4">
                                    <div style={{
                                        background: "#011C34",
                                        border: "2px solid #FFF",
                                        borderRadius: "16px",
                                        padding: "40px 30px",
                                    }}>
                                        {projectStatus?.status === "completed" ? (
                                            <>
                                                <div style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    background: "linear-gradient(135deg, #00ff88, #00cc70)",
                                                    borderRadius: "50%",
                                                    margin: "0 auto 25px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "30px",
                                                }}>
                                                    ✓
                                                </div>

                                                <h4 className="text-white mb-2" style={{ fontWeight: "600" }}>
                                                    Project Completed!
                                                </h4>
                                            </>
                                        ) : (
                                            <>
                                                <div style={{
                                                    width: "60px",
                                                    height: "60px",
                                                    background: "linear-gradient(135deg, #667eea, #764ba2)",
                                                    borderRadius: "50%",
                                                    margin: "0 auto 25px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    fontSize: "30px",
                                                    animation: "pulse 2s infinite",
                                                }}>
                                                    ⚙️
                                                </div>

                                                <h4 className="text-white mb-2" style={{ fontWeight: "600" }}>
                                                    Generating Project...
                                                </h4>
                                            </>
                                        )}

                                        <p className="text-white mb-3" style={{
                                            fontSize: "14px",
                                            opacity: 0.8,
                                        }}>
                                            {projectStatus?.status === "completed"
                                                ? "Your project is ready for download"
                                                : "Please wait while we build your project"}
                                        </p>

                                        <div className="d-flex align-items-center justify-content-center gap-3 mb-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <span className="text-white" style={{ fontSize: "14px", opacity: 0.7 }}>
                                                    Project:
                                                </span>
                                                <span className="text-warning" style={{
                                                    fontSize: "16px",
                                                    fontWeight: "bold",
                                                }}>
                                                    {projectName}
                                                </span>
                                            </div>
                                        </div>

                                        <div style={{
                                            background: "rgba(255, 255, 255, 0.05)",
                                            borderRadius: "10px",
                                            padding: "12px 20px",
                                            display: "inline-block",
                                        }}>
                                            <span className="text-white" style={{ fontSize: "14px", opacity: 0.7 }}>
                                                Time Elapsed:
                                            </span>
                                            <span className="text-white" style={{
                                                fontSize: "18px",
                                                fontWeight: "bold",
                                                marginLeft: "10px",
                                            }}>
                                                {formatElapsedTime(elapsedTime)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <style jsx>{`
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
            opacity: 1;
        }
        50% {
            transform: scale(1.05);
            opacity: 0.8;
        }
    }
`}</style>

                                {projectStatus && (
                                    <div className="mb-4">
                                        {/* Progress Bar */}
                                        <div className="mb-3">
                                            <div style={{
                                                background: "#011C34",
                                                border: "2px solid #FFF",
                                                borderRadius: "12px",
                                                padding: "15px",
                                            }}>
                                                <p className="text-white mb-2">
                                                    <strong>Progress: {projectStatus.progress}%</strong>
                                                </p>
                                                <div style={{
                                                    width: "100%",
                                                    height: "30px",
                                                    background: "#001f3f",
                                                    borderRadius: "8px",
                                                    overflow: "hidden",
                                                }}>
                                                    <div style={{
                                                        width: `${projectStatus.progress}%`,
                                                        height: "100%",
                                                        background: "linear-gradient(90deg, #00ff88, #00cc70)",
                                                        transition: "width 0.5s ease",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                    }}>
                                                        <span style={{ color: "#000", fontWeight: "bold", fontSize: "12px" }}>
                                                            {projectStatus.progress}%
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Current Status */}
                                        <div className="mb-3">
                                            <div style={{
                                                background: "#011C34",
                                                border: "2px solid #FFF",
                                                borderRadius: "12px",
                                                padding: "15px",
                                            }}>
                                                <p className="text-white mb-2">
                                                    <strong>Status:</strong> {projectStatus.currentStep}
                                                </p>
                                                <p className="text-white mb-0">
                                                    <strong>Current File:</strong> {projectStatus.currentFile}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Files Progress */}
                                        <div className="mb-3">
                                            <div style={{
                                                background: "#011C34",
                                                border: "2px solid #FFF",
                                                borderRadius: "12px",
                                                padding: "15px",
                                            }}>
                                                <p className="text-white mb-2">
                                                    <strong>Files Generated:</strong> {projectStatus.filesGenerated} / {projectStatus.totalExpectedFiles}
                                                </p>
                                                <p className="text-white mb-0">
                                                    <strong>Pending Files:</strong> {projectStatus.pendingFiles}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Component Status */}
                                        <div className="mb-3">
                                            <div style={{
                                                background: "#011C34",
                                                border: "2px solid #FFF",
                                                borderRadius: "12px",
                                                padding: "15px",
                                            }}>
                                                <p className="text-white mb-3">
                                                    <strong>Component Progress:</strong>
                                                </p>

                                                {/* Backend */}
                                                {projectStatus.componentStatus?.backend && (
                                                    <div className="mb-2">
                                                        <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                            Backend: {projectStatus.componentStatus.backend.generated} / {projectStatus.componentStatus.backend.total} ({projectStatus.componentStatus.backend.progress}%)
                                                        </p>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "20px",
                                                            background: "#001f3f",
                                                            borderRadius: "5px",
                                                            overflow: "hidden",
                                                        }}>
                                                            <div style={{
                                                                width: `${projectStatus.componentStatus.backend.progress}%`,
                                                                height: "100%",
                                                                background: "#3b82f6",
                                                                transition: "width 0.5s ease",
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Frontend */}
                                                {projectStatus.componentStatus?.frontend && (
                                                    <div className="mb-2">
                                                        <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                            Frontend: {projectStatus.componentStatus.frontend.generated} / {projectStatus.componentStatus.frontend.total} ({projectStatus.componentStatus.frontend.progress}%)
                                                        </p>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "20px",
                                                            background: "#001f3f",
                                                            borderRadius: "5px",
                                                            overflow: "hidden",
                                                        }}>
                                                            <div style={{
                                                                width: `${projectStatus.componentStatus.frontend.progress}%`,
                                                                height: "100%",
                                                                background: "#10b981",
                                                                transition: "width 0.5s ease",
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Mobile */}
                                                {projectStatus.componentStatus?.mobile && (
                                                    <div className="mb-0">
                                                        <p className="text-white mb-1" style={{ fontSize: "13px" }}>
                                                            Mobile: {projectStatus.componentStatus.mobile.generated} / {projectStatus.componentStatus.mobile.total} ({projectStatus.componentStatus.mobile.progress}%)
                                                        </p>
                                                        <div style={{
                                                            width: "100%",
                                                            height: "20px",
                                                            background: "#001f3f",
                                                            borderRadius: "5px",
                                                            overflow: "hidden",
                                                        }}>
                                                            <div style={{
                                                                width: `${projectStatus.componentStatus.mobile.progress}%`,
                                                                height: "100%",
                                                                background: "#f59e0b",
                                                                transition: "width 0.5s ease",
                                                            }}></div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Show message if no components */}
                                                {!projectStatus.componentStatus?.backend &&
                                                    !projectStatus.componentStatus?.frontend &&
                                                    !projectStatus.componentStatus?.mobile && (
                                                        <p className="text-white mb-0" style={{ fontSize: "13px" }}>
                                                            No component breakdown available
                                                        </p>
                                                    )}
                                            </div>
                                        </div>

                                        {/* Continue Button */}
                                        {projectStatus.progress < 100 && projectStatus.needsContinuation === false && (
                                            <div className="text-center mb-3">
                                                <Button
                                                    sx={{
                                                        padding: "8px 30px",
                                                        background: "#ff9800!important",
                                                        color: "white!important",
                                                    }}
                                                    onClick={handleContinueGeneration}
                                                    variant="contained"
                                                    disabled={isContinuing}
                                                >
                                                    {isContinuing ? "Continuing..." : "Continue Generation"}
                                                </Button>
                                            </div>
                                        )}

                                        {/* Download Button */}
                                        {projectStatus.status === "completed" && (
                                            <div className="text-center mb-5">
                                                <div style={{
                                                    background: "#011C34",
                                                    border: "2px solid #00ff88",
                                                    borderRadius: "16px",
                                                    padding: "40px 30px",
                                                    maxWidth: "500px",
                                                    margin: "0 auto",
                                                }}>
                                                    <div style={{
                                                        width: "70px",
                                                        height: "70px",
                                                        background: "linear-gradient(135deg, #00ff88, #00cc70)",
                                                        borderRadius: "50%",
                                                        margin: "0 auto 25px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "35px",
                                                    }}>
                                                        ✓
                                                    </div>

                                                    <h4 className="text-white mb-2" style={{ fontWeight: "600" }}>
                                                        Your Project is Ready!
                                                    </h4>

                                                    <p className="text-white mb-4" style={{
                                                        fontSize: "14px",
                                                        opacity: 0.8,
                                                    }}>
                                                        Download your project files and start developing
                                                    </p>

                                                    <Button
                                                        sx={{
                                                            padding: "12px 40px",
                                                            background: "linear-gradient(135deg, #00ff88, #00cc70)!important",
                                                            color: "#000!important",
                                                            fontWeight: "bold",
                                                            fontSize: "16px",
                                                            borderRadius: "10px",
                                                            textTransform: "none",
                                                            "&:hover": {
                                                                background: "linear-gradient(135deg, #00cc70, #00ff88)!important",
                                                                transform: "translateY(-2px)",
                                                                boxShadow: "0 5px 20px rgba(0, 255, 136, 0.4)",
                                                            },
                                                            transition: "all 0.3s ease",
                                                        }}
                                                        onClick={handleDownloadProject}
                                                        variant="contained"
                                                        disabled={isDownloading}
                                                    >
                                                        {isDownloading ? "Downloading..." : "Download Project"}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {!projectStatus && (
                                    <div className="text-center mb-5">
                                        <Spin size="large" className="pt-3 pb-2" />
                                        <p className="text-center pb-2 d-block text-white mb-0">
                                            Initializing project generation...
                                        </p>
                                    </div>
                                )}

                                <div className="animate text-md-center mt-4 text-lg-center text-center mb-5 d-flex justify-content-center force-small-font">
                                    Please do not navigate away from the browser.
                                    <br /> Doing so will stop the process.
                                </div>
                                <div className="text-md-center text-white text-lg-center text-center d-flex justify-content-center force-small-font mt-1">
                                    While AI strives for accuracy, it may occasionally make mistakes. <br />Please verify any important information.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}